import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { DIM_COLOR_MO } from '@/theme/dimTokens';

/**
 * 번역 언어 선택 팝업 styled — 모바일(≤768px)은 풀스크린 bottom-sheet.
 * breakpoint 는 index.tsx 의 useMediaQuery 와 동일 유지.
 */

const MOBILE_QUERY = '@media screen and (max-width: 768px)';

/** `.translation-menu` 루트 — 데스크탑 absolute 팝업 / 모바일 풀스크린 fixed */
export const TranslationMenuRoot = styled(Box)`
  position: absolute;
  top: 30px;
  left: -270px;
  z-index: 11;

  ${MOBILE_QUERY} {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100vh;
  }
`;

/** 모바일 bottom-sheet 딤 backdrop — 데스크탑 hidden. MUI Backdrop 이 아니라 테마 오버라이드가 안 닿음 */
export const MobileBackdrop = styled(Box)`
  display: none;

  ${MOBILE_QUERY} {
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: ${DIM_COLOR_MO};
    z-index: 0;
  }
`;

/** `.translation-menu-wrap` — 데스크탑 가로 배치 / 모바일 bottom-sheet 세로 stack */
export const TranslationMenuWrap = styled(Box)`
  position: relative;
  display: flex;
  flex-direction: row;
  gap: 3px;

  ${MOBILE_QUERY} {
    flex-direction: column;
    gap: 0;
    position: absolute;
    top: auto;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    z-index: 1;
    background-color: var(--dk-bg, #fff);
    border-radius: 20px 20px 0 0;
    overflow: hidden;
    /* Figma: v2.01 Effect/BottomSheet = drop-shadow(0 -16px 16px rgba(0,0,0,0.08)). */
    filter: drop-shadow(0 -16px 16px rgba(0, 0, 0, 0.08));
  }
`;

/** `.menu-box` — 데스크탑 300px 그라데이션 보더 박스 / 모바일 풀폭 플레인 */
export const MenuBox = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  width: 300px;
  height: fit-content;
  padding: 16px;
  border-radius: 8px;
  background: linear-gradient(var(--dk-bg, #ffffff), var(--dk-bg, #ffffff)) padding-box,
    linear-gradient(
        135deg,
        rgba(225, 179, 223, 1),
        rgba(175, 178, 244, 1),
        rgba(225, 179, 223, 1)
      )
      border-box;
  border: 2px solid transparent;
  box-shadow: 0 16px 32px 0 rgba(0, 0, 0, 0.08);

  ${MOBILE_QUERY} {
    width: 100%;
    border-color: var(--dk-bg, #fff);
    border-radius: 0;
    box-shadow: none;
    background: var(--dk-bg, #ffffff);
  }
`;

/** `.btn-languge-selector` — 모바일은 탭 영역 확보 위해 패딩 확대 */
export const LanguageSelectorButton = styled('button')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 5px;
  border: none;
  border-radius: 6px;
  color: var(--dk-text-secondary, #888d96);
  background-color: transparent;
  cursor: pointer;
  font-family: inherit;
  font-size: inherit;

  &:hover,
  &:focus,
  &:active {
    background-color: var(--dk-purple-soft, #fbf4fa);
  }

  ${MOBILE_QUERY} {
    padding: 8px 12px;
  }
`;

/** `.translation-menu .menu-box.menu-depth1 .selecte-language-info` */
export const SelectedLanguageInfo = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-direction: row;
  flex-wrap: nowrap;
  gap: 8px;

  ${MOBILE_QUERY} {
    font-size: 18px;
  }
`;

/** `.translation-menu .menu-box.menu-depth1 .selecte-language-info .bagic-text` */
export const BagicText = styled('span')`
  width: 41px;
  text-align: left;

  ${MOBILE_QUERY} {
    width: 50px;
  }
`;

/** `.translation-menu .menu-box.menu-depth1 .translation-current-language` (+ .selected) */
export const CurrentLanguage = styled('span', {
  shouldForwardProp: (p) => p !== 'selected',
})<{ selected?: boolean }>(({ selected }) => ({
  display: 'inline-block',
  width: 186,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  wordWrap: 'break-word',
  textOverflow: 'ellipsis',
  textAlign: 'left',
  ...(selected && { color: 'var(--dk-text, #000)', fontWeight: 500 }),
  // 모바일 선택 상태: viewport 기준 폭으로 늘려 긴 언어명도 한 줄 표시.
  [MOBILE_QUERY]: {
    ...(selected && { width: 'calc(100vw - 130px)' }),
  },
}));

/** `.translation-menu .menu-box.menu-depth1 .icon-arrow` (chevron #C77AC4) */
export const IconArrowWrap = styled(Box)`
  padding: 0 7px;
  display: inline-flex;
  align-items: center;
  color: #c77ac4;
`;

/** 번역 아이콘 anchor wrapper — `.translation-menu` 가 absolute 라 positioned 부모 필요 */
export const TranslationIconAnchor = styled(Box)`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

// ===== depth2 (언어 리스트) =====

/**
 * `.menu-depth2` — MenuBox 시각 상속 + 절대 위치/최대 높이/내부 스크롤.
 * alignSide 기본 right, 우측 공간 부족 시 index.tsx 가 left 로 전환.
 */
export const MenuBoxDepth2 = styled(MenuBox, {
  shouldForwardProp: (p) => p !== 'alignSide',
})<{ alignSide?: 'left' | 'right' }>(({ alignSide = 'right' }) => ({
  position: 'absolute',
  // 정적 fallback. 컴포넌트에서 useLayoutEffect 가 viewport 기준
  top: 40,
  height: 'auto',
  maxHeight: 'calc(100vh - 80px)',
  zIndex: 11,
  overflowY: 'auto',
  ...(alignSide === 'right'
    ? { left: '100%', right: 'auto', marginLeft: 3 }
    : { right: '100%', left: 'auto', marginRight: 3 }),
  /** 모바일에서는 absolute 가 아닌 column flow 안의 static 박스 */
  [MOBILE_QUERY]: {
    position: 'static',
    top: 'auto',
    left: 'auto',
    right: 'auto',
    marginLeft: 0,
    marginRight: 0,
    maxHeight: 'calc(100vh - 149px)',
    fontSize: 18,
  },
}));

/** `.recent-languages` — 모바일은 회색 본문 라벨 톤 */
export const RecentLanguagesLabel = styled('span')`
  margin-left: 4px;
  color: var(--dk-text-secondary, #888d96);

  ${MOBILE_QUERY} {
    display: block;
    width: 100%;
    margin-left: 0;
    padding: 4px;
    box-sizing: border-box;
    font-size: 18px;
    line-height: 26px;
  }
`;

/** `.translation-language-list` */
export const LanguageList = styled('ul')`
  width: 100%;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: var(--dk-text, #000);
  text-align: left;
  list-style: none;
`;

/** `.translation-language-item` (+ 3번째 아이템 하단 divider) */
export const LanguageListItem = styled('li')`
  &:nth-of-type(3):not(:last-child)::after {
    content: '';
    display: block;
    margin: 5px 0;
    width: 100%;
    height: 1px;
    background-color: var(--dk-bg-strong, #e7e9ec);
  }
`;

/** `.translation-language-button` (+ 선택 강조) */
export const LanguageButton = styled('button', {
  shouldForwardProp: (p) => p !== 'active',
})<{ active?: boolean }>(({ active }) => ({
  display: 'block',
  width: '100%',
  padding: 4,
  border: 'none',
  borderRadius: 6,
  background: 'transparent',
  textAlign: 'left',
  justifyContent: 'flex-start',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 'inherit',
  color: 'var(--dk-text, #000)',
  fontWeight: active ? 500 : 400,
  '&:hover': { backgroundColor: 'var(--dk-purple-soft, #fbf4fa)' },
  '&:focus': { backgroundColor: 'var(--dk-purple-soft, #fbf4fa)', outline: 'none' },
}));

// ===== 모바일 전용 (bottom-sheet 헤더 + 액션 버튼) =====
// 데스크탑은 렌더 자체를 안 하므로 별도 hide 불필요.

/** `.menu-title` — 모바일 bottom-sheet 헤더 */
export const MobileMenuTitle = styled(Box, {
  shouldForwardProp: (p) => p !== 'isBack',
})<{ isBack?: boolean }>(({ isBack }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: isBack ? 'flex-start' : 'center',
  position: 'relative',
  width: '100%',
  minHeight: 56,
  padding: '14px 16px',
  // 상단 라운드는 TranslationMenuWrap 에서 overflow:hidden 으로 일괄 처리. 본 헤더는 평평한 사각형.
  backgroundColor: 'var(--dk-bg, #fff)',
  fontSize: 16,
  fontWeight: 500,
  color: 'var(--dk-text, #000)',
  borderBottom: '1px solid var(--dk-line, #e7e9ec)',
  boxSizing: 'border-box',
  gap: 12,
}));

/** 헤더 좌측 번역 아이콘 (depth1 진입 시) */
export const MobileMenuTitleTranslationIcon = styled('span')`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #c77ac4;

  & > svg {
    color: #c77ac4;
    flex-shrink: 0;
  }
`;

/** 헤더 중앙 "번역하기" 텍스트 — depth2 는 미사용(back 버튼에 텍스트 포함) */
export const MobileMenuTitleCenterText = styled('span')`
  display: inline-flex;
  align-items: center;
  font-family: inherit;
  font-size: 16px;
  font-weight: 500;
  color: var(--dk-text, #000);
`;

/** 헤더 좌측 back 버튼 (depth2 진입 시) */
export const MobileMenuTitleBackButton = styled('button')`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  color: var(--dk-text, #000);
  font-family: inherit;
  font-size: 16px;
  font-weight: 500;

  & > svg {
    color: #c77ac4;
    flex-shrink: 0;
  }
`;

/** 헤더 우측 X 닫기 버튼 — 메뉴 전체 닫음 */
export const MobileCloseButton = styled('button')`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background-color: transparent;
  color: var(--dk-text-secondary, #888d96);
  cursor: pointer;
  line-height: 1;

  &:hover,
  &:focus,
  &:active {
    background-color: transparent;
    color: var(--dk-text, #000);
  }
`;

/** `.btn-go-translate` — 모바일 depth1 하단 풀폭 검정 "번역 시작" 버튼 */
export const MobileGoTranslateButton = styled('button')`
  margin: 16px;
  width: calc(100% - 32px);
  padding: 12px;
  color: #fff;
  background-color: #000;
  border: none;
  border-radius: 8px;
  font-family: inherit;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;

  &:hover:not(:disabled),
  &:focus:not(:disabled),
  &:active:not(:disabled) {
    background-color: #333;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;
